import Cookies from 'js-cookie';
import { settings } from 'settings';
import { ERROR } from 'const/error';
import { getAuthHeadersFromCookie, setCookie } from 'lib/AuthClient/cookie';
import firebase from 'firebase/app';
import { AuthHeaders, UserAccessInformation } from './cookieTypes';
import dayjs from 'dayjs';

const HEADER_JSON_TYPE = 'application/json';

export async function signOut() {
  Cookies.remove('authHeaders');
  Cookies.remove('role');
  Cookies.remove('roleId');
  Cookies.remove('prefs');
  await firebase.auth().signOut();
}

export async function signOutAndRedirect() {
  await signOut();
  window.location.href = '/sign-in';
}

export function initFirebase() {
  const firebaseConfig = { apiKey: settings.firebaseKey };

  firebase.initializeApp(firebaseConfig);
}

export async function fetchUserAccessInformation(
  sessionToken: string
): Promise<UserAccessInformation> {
  const roleResponse: Response = await fetch(
    `${settings.newApiUrl}/user/access`,
    { method: 'GET', headers: { Authorization: `Bearer ${sessionToken}` } }
  );

  if (!roleResponse.ok) throw Error(ERROR.FETCH_ROLES_BACKEND);
  return (await roleResponse.json()) as UserAccessInformation;
}

export async function refreshSession(): Promise<AuthHeaders> {
  const authHeaders: AuthHeaders = getAuthHeadersFromCookie();

  if (
    !authHeaders ||
    hoursTillTokenExpiration(authHeaders.Authorization) < 12 ||
    !authHeaders.role ||
    !authHeaders.availableRegions
  ) {
    // Refresh if less than 12 hours till session is expired
    const newAuthHeaders: {
      user: firebase.auth.UserCredential;
      token: string;
    } = await sessionRefresh(authHeaders);

    createAuthCookie(
      newAuthHeaders.token,
      newAuthHeaders.user,
      await fetchUserAccessInformation(newAuthHeaders.token)
    );
  }

  return authHeaders;
}

export function createAuthCookie(
  sessionToken: string,
  response: firebase.auth.UserCredential,
  userAccess: UserAccessInformation
) {
  const authHeaders: AuthHeaders = {
    Authorization: `Bearer ${sessionToken}`,
    authorizationBackend: settings.newApiUrl,
    uid: response.user.uid,
    email: response.user.email,
    username: response.additionalUserInfo.username
      ? response.additionalUserInfo.username
      : response.user.email,
    role: userAccess.role,
    availableRegions: userAccess.regions,
    limitedAccess: userAccess.limitedAccess,
  };
  setCookie('authHeaders', JSON.stringify(authHeaders));
}

function hoursTillTokenExpiration(authorizationHeader: string) {
  const tokenPayload = (authorizationHeader.split('.')[1] || '').trim();
  const decodedToken = atob(tokenPayload);
  const tokenClaims = JSON.parse(decodedToken);
  const { exp } = tokenClaims;
  const expiryDate = dayjs(exp * 1000);
  const now = dayjs();

  return expiryDate.diff(now, 'hour');
}

export async function sessionLogin(idToken) {
  const sessionLoginResponse = await fetch(
    `${settings.newApiUrl}/auth/session-login`,
    {
      method: 'POST',
      headers: {
        Accept: HEADER_JSON_TYPE,
        'Content-Type': HEADER_JSON_TYPE,
      },
      body: JSON.stringify({ idToken: idToken }),
    }
  );

  return (await sessionLoginResponse.json()).token;
}

async function exchangeSessionTokenForCustomToken(
  authHeaders: any
): Promise<string> {
  const response: Response = await fetch(
    `${settings.newApiUrl}/auth/session-refresh`,
    {
      method: 'POST',
      headers: {
        Accept: HEADER_JSON_TYPE,
        'Content-Type': HEADER_JSON_TYPE,
        ...authHeaders,
      },
    }
  );

  if (!response.ok) throw Error(await response.text());

  return (await response.json()).token;
}

async function sessionRefresh(
  authHeaders: AuthHeaders
): Promise<{ user: firebase.auth.UserCredential; token: string }> {
  const customToken = await exchangeSessionTokenForCustomToken(authHeaders);
  const userCredential = await firebase
    .auth()
    .signInWithCustomToken(customToken);
  const token = await userCredential.user.getIdToken();
  const sessionToken = await sessionLogin(token);

  return { user: userCredential, token: sessionToken };
}
